'use client';

import { Loader2 } from 'lucide-react';
import { useTranslations } from 'next-intl';
import { type ReactNode, useState } from 'react';
import {
  useCommentReactions,
  useCreateComment,
  useDeleteComment,
  useEditComment,
} from '@/lib/api/comments';
import { GlowName } from '@/lib/components/RewardsHub/GlowName';
import { useAuthSession } from '@/lib/hooks/useAuth';
import { Link } from '@/lib/routing';
import { AnimatedReactionButton } from '@/lib/ui-kit/animated-reaction-button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/lib/ui-kit/dropdown-menu';
import { Icon } from '@/lib/ui-kit/icons';
import { Text } from '@/lib/ui-kit/text';
import { Textarea } from '@/lib/ui-kit/textarea';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/lib/ui-kit/tooltip';
import { cn } from '@/lib/ui-kit/utils';
import { Button } from '@/lib/ui-kit-v2/button';
import { UserAvatar } from '@/lib/ui-kit-v2/user-avatar';
import { formatTimeDate } from '@/lib/utils/dates';
import { formatRelativeTime } from '@/lib/utils/formatRelativeTime';
import { truncate } from '@/lib/utils/truncate';
import type { ThreadedComment } from './buildThreadedComments';
import { CommentReplyForm } from './CommentReplyForm';
import { useCommentsContext } from './CommentsContext';
import { MAX_COMMENT_LENGTH } from './constants';
import { GifMessage } from './GifMessage';
import { buildGifMarker, parseGifMarker } from './gif';

const TEXT_EXPAND_THRESHOLD = 300;

interface CommentItemProps {
  comment: ThreadedComment;
  depth?: number;
}

export function CommentItem({ comment, depth = 0 }: CommentItemProps) {
  const { source } = useCommentsContext();
  const namespace = source.kind === 'event' ? 'eventComments' : 'comments';
  const t = useTranslations(namespace);
  const { isAuthenticated } = useAuthSession();
  const { likeComment, dislikeComment } = useCommentReactions(source);
  const createComment = useCreateComment(source);
  const editComment = useEditComment(source);
  const deleteComment = useDeleteComment(source);

  const [isEditing, setIsEditing] = useState(false);
  const [editContent, setEditContent] = useState(comment.content);
  const [isReplying, setIsReplying] = useState(false);
  const [replyContent, setReplyContent] = useState('');
  const [textExpanded, setTextExpanded] = useState(false);
  const [repliesExpanded, setRepliesExpanded] = useState(depth === 0);

  const isOwner = comment.isMine;
  const isDeleted = comment.isDeleted;
  const canModify = Date.now() - new Date(comment.createdAt).getTime() < 15 * 60 * 1000;
  const isMutating = createComment.isPending || editComment.isPending || deleteComment.isPending;
  const isCardDimmed = editComment.isPending || deleteComment.isPending;

  const authorName = comment.author || t('anonymous');
  const profileHref = comment.outpollUserId ? `/stats/profile/${comment.outpollUserId}` : null;
  const profileLabel = t('viewProfileOf', { name: authorName });
  const renderProfileLink = (children: ReactNode, className?: string): ReactNode => {
    if (!profileHref) return children;
    return (
      <Link
        href={profileHref}
        target="_blank"
        rel="noopener noreferrer"
        aria-label={profileLabel}
        className={className}
      >
        {children}
      </Link>
    );
  };
  const replyToName = comment.replyTo ? comment.replyTo.author || t('anonymous') : null;
  const replyToHref = comment.replyTo?.outpollUserId
    ? `/stats/profile/${comment.replyTo.outpollUserId}`
    : null;
  // 12 characters + "..." like Polymarket's reply chip; the full name is in the title.
  const replyToLabel = replyToName ? `@${truncate(replyToName, 15)}` : null;
  const replyToClassName =
    'w-fit text-[13px] font-medium leading-5 tracking-[-0.01em] text-v2-bg-blue';
  const repliesCount = comment.replies.length;
  const isLiked = comment.userReaction === 'LIKE';
  const isDisliked = comment.userReaction === 'DISLIKE';
  const likesCount = comment.likesCount ?? 0;
  const dislikesCount = comment.dislikesCount ?? 0;
  const gifId = parseGifMarker(comment.content);
  const contentLength = comment.content?.length ?? 0;
  const needsTextExpand = contentLength > TEXT_EXPAND_THRESHOLD;
  const editCharCount = editContent.length;
  const isEditOverLimit = editCharCount > MAX_COMMENT_LENGTH;
  const showEditCounter = editCharCount >= MAX_COMMENT_LENGTH * 0.9;

  const relativeTime = formatRelativeTime(comment.createdAt, {
    justNow: t('justNow'),
    minutesAgo: (count: number) => t('minutesAgo', { count }),
    hoursAgo: (count: number) => t('hoursAgo', { count }),
    daysAgo: (count: number) => t('daysAgo', { count }),
    weeksAgo: (count: number) => t('weeksAgo', { count }),
    monthsAgo: (count: number) => t('monthsAgo', { count }),
    yearsAgo: (count: number) => t('yearsAgo', { count }),
  });
  const fullTimestamp = formatTimeDate(comment.createdAt);

  const handleLike = () => {
    if (!isAuthenticated) return;
    likeComment.mutate({ commentId: comment.id, currentReaction: comment.userReaction });
  };

  const handleDislike = () => {
    if (!isAuthenticated) return;
    dislikeComment.mutate({ commentId: comment.id, currentReaction: comment.userReaction });
  };

  const handleReplyToggle = () => {
    setIsReplying((prev) => !prev);
    setReplyContent('');
  };

  const handleReplyCancel = () => {
    setIsReplying(false);
    setReplyContent('');
  };

  const handleReplySend = () => {
    if (!replyContent.trim() || replyContent.length > MAX_COMMENT_LENGTH) return;
    createComment.mutate(
      { content: replyContent.trim(), parentCommentId: comment.id },
      {
        onSuccess: () => {
          setIsReplying(false);
          setReplyContent('');
          setRepliesExpanded(true);
        },
      },
    );
  };

  const handleReplyGif = (id: string) => {
    createComment.mutate(
      { content: buildGifMarker(id), parentCommentId: comment.id },
      {
        onSuccess: () => {
          setIsReplying(false);
          setReplyContent('');
          setRepliesExpanded(true);
        },
      },
    );
  };

  const handleEditStart = () => {
    setEditContent(comment.content);
    setIsEditing(true);
  };

  const handleEditCancel = () => {
    setIsEditing(false);
    setEditContent(comment.content);
  };

  const handleEditSave = () => {
    if (!editContent.trim() || isEditOverLimit) return;
    editComment.mutate(
      { commentId: comment.id, content: editContent.trim() },
      { onSuccess: () => setIsEditing(false) },
    );
  };

  const handleDelete = () => {
    deleteComment.mutate({ commentId: comment.id, deletedContent: t('deletedComment') });
  };

  return (
    <div
      id={`comment-${comment.id}`}
      className={cn(
        '-mx-2 flex gap-3 rounded-lg px-2 py-1 transition-[background-color,opacity] duration-500 data-[highlight]:bg-v2-bg-blue/10',
        isCardDimmed && 'opacity-50',
      )}
    >
      {renderProfileLink(
        <UserAvatar
          userId={comment.outpollUserId}
          name={authorName}
          look={comment.look}
          size={40}
        />,
        profileHref ? 'shrink-0 cursor-pointer rounded-full outline-none' : undefined,
      )}

      <div className="flex min-w-0 flex-1 flex-col gap-2">
        {/* Header: tags + name + time, More icon */}
        <div className="flex items-center justify-between gap-2">
          <div className="flex min-w-0 items-center gap-2">
            {isOwner && (
              <span
                className={cn(
                  'shrink-0 rounded-full bg-blue-500 px-2 py-1 text-[12px] font-medium leading-4',
                  'text-white tracking-[-0.01em]',
                )}
              >
                {t('you')}
              </span>
            )}
            {renderProfileLink(
              <Text className="truncate text-[13px] font-medium leading-4 tracking-[-0.01em] text-v2-text-primary">
                <GlowName glow={comment.look?.glow ?? null}>{authorName}</GlowName>
              </Text>,
              profileHref ? 'min-w-0 cursor-pointer outline-none hover:underline' : undefined,
            )}
            <Tooltip>
              <TooltipTrigger asChild>
                <Text className="shrink-0 cursor-pointer text-[13px] font-medium leading-4 tracking-[-0.01em] text-v2-text-primary opacity-50">
                  {relativeTime}
                </Text>
              </TooltipTrigger>
              <TooltipContent className="px-3 py-2 text-[12px] leading-4">
                {fullTimestamp}
              </TooltipContent>
            </Tooltip>
          </div>

          {isOwner && !isDeleted && canModify && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  type="button"
                  className="-my-1 shrink-0 cursor-pointer p-1 text-v2-text-secondary transition-colors hover:text-v2-text-primary disabled:cursor-default disabled:opacity-50"
                  disabled={isMutating}
                >
                  <Icon name="EllipsisVertical" size={16} className="rotate-90" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                align="end"
                className="bg-v2-surface-secondary border-v2-border text-v2-text-primary min-w-[140px] p-1"
              >
                {!gifId && (
                  <DropdownMenuItem
                    onSelect={handleEditStart}
                    disabled={isMutating}
                    className="text-v2-text-primary focus:bg-v2-neutral-surface focus:text-v2-text-primary [&_svg:not([class*='text-'])]:text-v2-icon-primary"
                  >
                    <Icon name="Edit" size={16} />
                    {t('edit')}
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem
                  variant="destructive"
                  onSelect={handleDelete}
                  disabled={isMutating}
                  className="text-v2-btn-premium-red-light! focus:bg-v2-red-surface! focus:text-v2-btn-premium-red-light! [&_svg]:text-v2-btn-premium-red-light!"
                >
                  <Icon name="Close" size={16} />
                  {t('delete')}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>

        {/* Body: edit form OR comment text */}
        {isEditing ? (
          <div className="flex min-w-0 flex-col gap-3">
            <div className="flex flex-col gap-1">
              <div
                className={cn(
                  'flex min-w-0 items-start rounded-[10px] border px-4 py-3 transition-colors',
                  isEditOverLimit
                    ? 'border-destructive'
                    : 'border-v2-text-secondary focus-within:border-v2-text-primary',
                )}
              >
                <Textarea
                  value={editContent}
                  onChange={(e) => setEditContent(e.target.value)}
                  placeholder={t('yourMessage')}
                  autoFocus
                  disabled={editComment.isPending}
                  className={cn(
                    'box-border max-h-[200px]! min-h-6! w-full min-w-0 flex-1 rounded-none! border-0! bg-transparent! p-0!',
                    'text-[13px]! leading-[150%]! text-v2-text-primary! outline-none! ring-0! wrap-anywhere',
                    'placeholder:text-v2-text-secondary! focus:outline-none! focus:ring-0!',
                    'disabled:cursor-not-allowed disabled:opacity-50',
                  )}
                />
              </div>
              {(isEditOverLimit || showEditCounter) && (
                <div className="flex items-center justify-between gap-2 text-[12px] leading-4">
                  <span className={cn('text-destructive', !isEditOverLimit && 'invisible')}>
                    {t('commentTooLong', { max: MAX_COMMENT_LENGTH })}
                  </span>
                  <span className={isEditOverLimit ? 'text-destructive' : 'text-v2-text-secondary'}>
                    {editCharCount}/{MAX_COMMENT_LENGTH}
                  </span>
                </div>
              )}
            </div>
            <div className="flex items-center justify-end gap-2">
              <Button
                type="button"
                appearance="ghost"
                size="M"
                onClick={handleEditCancel}
                disabled={editComment.isPending}
              >
                {t('cancel')}
              </Button>
              <Button
                type="button"
                appearance="accent"
                size="M"
                onClick={handleEditSave}
                disabled={!editContent.trim() || isMutating || isEditOverLimit}
              >
                {editComment.isPending ? <Loader2 className="size-4 animate-spin" /> : t('save')}
              </Button>
            </div>
          </div>
        ) : (
          <>
            <div className="flex flex-col">
              {replyToName &&
                (replyToHref ? (
                  <Link
                    href={replyToHref}
                    target="_blank"
                    rel="noopener noreferrer"
                    title={replyToName}
                    className={cn(replyToClassName, 'hover:underline')}
                  >
                    {replyToLabel}
                  </Link>
                ) : (
                  <span title={replyToName} className={replyToClassName}>
                    {replyToLabel}
                  </span>
                ))}
              {gifId ? (
                <GifMessage id={gifId} dimmed={isDeleted} />
              ) : (
                <>
                  <Text
                    as="div"
                    className={cn(
                      'whitespace-pre-wrap break-words text-[15px] font-normal leading-[150%] text-v2-text-primary',
                      isDeleted ? 'opacity-50' : 'opacity-80',
                      needsTextExpand && !textExpanded ? 'line-clamp-3' : '',
                    )}
                  >
                    {comment.content}
                  </Text>
                  {needsTextExpand && (
                    <button
                      type="button"
                      className="mt-1 w-fit cursor-pointer text-[13px] font-medium leading-4 tracking-[-0.01em] text-v2-text-secondary transition-colors hover:text-v2-text-primary"
                      onClick={() => setTextExpanded((prev) => !prev)}
                    >
                      {textExpanded ? t('showLess') : t('readMore')}
                    </button>
                  )}
                </>
              )}
            </div>

            {/* Actions row: like, dislike, reply */}
            <div className="flex items-center gap-4 py-1">
              <AnimatedReactionButton
                isActive={isLiked}
                onClick={handleLike}
                disabled={isDeleted || !isAuthenticated}
                className={cn(
                  'group flex cursor-pointer items-center gap-1 disabled:cursor-default disabled:opacity-50',
                  !isLiked && 'opacity-50',
                )}
              >
                <Icon
                  name="Like"
                  size={20}
                  className={cn(
                    'transition-colors',
                    isLiked
                      ? '[&_path]:fill-v2-text-primary [&_path]:stroke-transparent'
                      : 'group-hover:[&_path]:stroke-v2-text-primary',
                  )}
                />
                {likesCount > 0 && (
                  <Text
                    className={cn(
                      'text-[13px] font-medium leading-4 tracking-[-0.01em] transition-colors',
                      'text-v2-text-primary',
                    )}
                  >
                    {likesCount}
                  </Text>
                )}
              </AnimatedReactionButton>

              <AnimatedReactionButton
                isActive={isDisliked}
                onClick={handleDislike}
                disabled={isDeleted || !isAuthenticated}
                className={cn(
                  'group flex cursor-pointer items-center gap-1 disabled:cursor-default disabled:opacity-50',
                  !isDisliked && 'opacity-50',
                )}
              >
                <Icon
                  name="Like"
                  size={20}
                  className={cn(
                    'rotate-180 transition-colors',
                    isDisliked
                      ? '[&_path]:fill-v2-text-primary [&_path]:stroke-transparent'
                      : 'group-hover:[&_path]:stroke-v2-text-primary',
                  )}
                />
                {dislikesCount > 0 && (
                  <Text
                    className={cn(
                      'text-[13px] font-medium leading-4 tracking-[-0.01em] transition-colors',
                      'text-v2-text-primary',
                    )}
                  >
                    {dislikesCount}
                  </Text>
                )}
              </AnimatedReactionButton>

              {!isDeleted && (
                <button
                  type="button"
                  className="cursor-pointer rounded-[10px] px-1 py-1 text-[13px] font-medium leading-4 tracking-[-0.01em] text-v2-text-primary transition-opacity hover:opacity-80 disabled:cursor-default disabled:opacity-50"
                  onClick={handleReplyToggle}
                  disabled={!isAuthenticated || isMutating}
                >
                  {t('reply')}
                </button>
              )}
            </div>

            {isReplying && (
              <CommentReplyForm
                value={replyContent}
                onValueChange={setReplyContent}
                onSend={handleReplySend}
                onCancel={handleReplyCancel}
                isSubmitting={createComment.isPending}
                isBlocked={editComment.isPending || deleteComment.isPending}
                placeholder={t('replyTo', { name: authorName })}
                sendLabel={t('send')}
                cancelLabel={t('cancel')}
                tooLongLabel={t('commentTooLong', { max: MAX_COMMENT_LENGTH })}
                onSelectGif={handleReplyGif}
              />
            )}

            {depth === 0 && repliesCount > 0 && (
              <div>
                <button
                  type="button"
                  className="flex h-6 cursor-pointer items-center gap-1 py-1 opacity-50 transition-opacity hover:opacity-100"
                  onClick={() => setRepliesExpanded((prev) => !prev)}
                >
                  <Text className="text-[13px] font-medium leading-4 tracking-[-0.01em] text-v2-text-primary">
                    {t('repliesCount', { count: repliesCount })}
                  </Text>
                  <Icon
                    name="ChevronDownRounded"
                    size={16}
                    className={cn(
                      'text-v2-text-primary transition-transform',
                      repliesExpanded && 'rotate-180',
                    )}
                  />
                </button>

                {repliesExpanded && (
                  <div className="mt-3 flex flex-col gap-3">
                    {comment.replies.map((reply) => (
                      <CommentItem key={reply.id} comment={reply} depth={depth + 1} />
                    ))}
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
